import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AIVisibilityContext = createContext({ aiEnabled: true, refresh: () => {} });

/**
 * AIVisibilityProvider — hydrates the global `ai_master_enabled` flag
 * once on mount and listens for the `atrium:ai-visibility-changed`
 * window event so the engineer-only kill switch in the admin panel
 * propagates to every tab/page instantly without a hard refresh.
 *
 * Any AI-related UI surface (Hero "AI Deep Research", HeavenlyAssistant,
 * "AI Polish", "Plan with AI", AI Render buttons, etc.) should consume
 * this via `useAIVisibility()` and return null when `aiEnabled === false`.
 */
export function AIVisibilityProvider({ children }) {
  const [aiEnabled, setAiEnabled] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/ai-settings/public`);
      setAiEnabled(data?.ai_master_enabled !== false);
    } catch {
      // If the public endpoint is unavailable, fall back to "enabled"
      // so we don't accidentally hide every AI button after a transient
      // network blip. Admin kill-switch must be an explicit OFF.
      setAiEnabled(true);
    }
  }, []);

  useEffect(() => {
    refresh();
    const onEvt = () => refresh();
    window.addEventListener('atrium:ai-visibility-changed', onEvt);
    return () => window.removeEventListener('atrium:ai-visibility-changed', onEvt);
  }, [refresh]);

  return (
    <AIVisibilityContext.Provider value={{ aiEnabled, refresh }}>
      {children}
    </AIVisibilityContext.Provider>
  );
}

export function useAIVisibility() {
  return useContext(AIVisibilityContext);
}

/**
 * <IfAIEnabled> — declarative wrapper used to hide an entire AI button
 * or AI-only section. Renders `children` when AI is on; renders `null`
 * (or the optional `fallback`) when the engineer kill-switch is active.
 */
export function IfAIEnabled({ children, fallback = null }) {
  const { aiEnabled } = useAIVisibility();
  return aiEnabled ? children : fallback;
}
